import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Inject, Injectable, RendererFactory2 } from '@angular/core';
import { PortalType } from '@ats/api/cm';
import {
	BehaviorSubject,
	catchError,
	distinctUntilChanged,
	filter,
	map,
	Observable,
	of,
	shareReplay,
	switchMap,
	take,
} from 'rxjs';
import {
	CLIENT_SERVICE_CONFIG,
	ClientConfig,
	ClientServiceConfig,
	ClientTheme,
	DEFAULT_CLIENT_CONFIG,
} from '../models';

const temporaryClientThemes: Map<string, string> = new Map([]);

@Injectable({
	providedIn: 'root',
})
export class ClientService {
	private readonly _client = new BehaviorSubject<string | null>(null);
	private readonly _theme = new BehaviorSubject<ClientTheme | null>(null);
	readonly client$: Observable<string> = this._client.asObservable().pipe(filter(Boolean));
	readonly theme$ = this._theme.asObservable();
	readonly config$: Observable<ClientConfig>;
	private readonly renderer = inject(RendererFactory2)?.createRenderer(null, null);

	constructor(
		@Inject(CLIENT_SERVICE_CONFIG) private readonly config: ClientServiceConfig,
		private readonly http: HttpClient,
		@Inject(DOCUMENT) private doc: Document
	) {
		this.config$ = this.fetchClientConfig$();
	}

	get clientSnapshot(): string | null {
		return this._client.getValue();
	}

	initialize$(client: string): Observable<boolean> {
		if (this.config.portal === PortalType.InternalPortal) {
			client = 'cmdemo';
		}
		this.setClient(client);
		this.setClientTheme(client);
		return this.client$.pipe(map(Boolean), take(1));
	}

	private setClient(client: string) {
		this._client.next(client);
	}

	private setClientTheme(client: string): void {
		client = temporaryClientThemes.get(client) || client;

		this.doc.body.classList.add('theme-default');
		if (client !== 'cmdemo') {
			const link = this.renderer.createElement('link');
			this.renderer.setAttribute(link, 'rel', 'stylesheet');
			this.renderer.setAttribute(link, 'type', 'text/css');
			this.renderer.setAttribute(link, 'href', `theme-${client}.css`);
			this.doc.head.appendChild(link);
			this.doc.body.classList.add(`theme-${client}`);
		}

		this._theme.next({
			name: client,
			logo: `/assets/client-assets/${client}/logo.png`,
			logoKO: `/assets/client-assets/${client}/logo-knockout.png`,
			logoEmail: `/assets/client-assets/${client}/logo-email.png`,
		});
	}

	private fetchClientConfig$(): Observable<ClientConfig> {
		return this.client$.pipe(
			filter(client => !!client),
			distinctUntilChanged(),
			switchMap(client =>
				this.http.get<ClientConfig & { sp?: ClientConfig; cp?: ClientConfig }>(
					`assets/client-assets/${client}/config.json`,
					{
						headers: {
							'X-bypass-token': 'true',
							'ichabod-suppress-error-log': 'true',
							'ichabod-suppress-error': 'true',
						},
					}
				)
			),
			catchError(() => of(null)),
			map(config => {
				return {
					...DEFAULT_CLIENT_CONFIG,
					...(config || {}),
					...(this.config.portal === PortalType.SponsorPortal
						? config?.sp || {}
						: config?.cp || {}),
				};
			}),
			shareReplay(1)
		);
	}
}
